import { useEffect, useRef, useState } from "react";
import styles from "./index.module.scss";

interface Option {
    label: string;
    value: string;
}

interface SelectFieldProps {
    label: string;
    options: Option[];
    value: string;
    name: string;
    onChange: (name: string, value: string) => void;
    minHeight?: number;
}

const SelectField = ({ label, options, value = "", name, onChange, minHeight = 40 }: SelectFieldProps) => {
    const [selectWidth, setSelectWidth] = useState(0);
    const [showOptions, setShowOptions] = useState(false);
    const selectRef = useRef<HTMLDivElement>(null);

    const handleSelectChange = (selectedValue: string) => {
        setShowOptions(false);
        onChange(name, selectedValue);
    };

    const handleOutsideClick = (e: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
            setShowOptions(false);
        }
    };

    const updateSelectWidth = () => {
        if (selectRef?.current) {
            setSelectWidth(selectRef.current.offsetWidth);
        }
    };

    useEffect(() => {
        updateSelectWidth()
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    return (
        <div className={styles.selectContainer} style={{ maxWidth: selectWidth ? `${selectWidth}px` : "" }}>
            <div className={styles.label}>{label}</div>
            <div
                className={`${styles.customSelect} ${showOptions ? styles.open : ''}`}
                onClick={() => setShowOptions(!showOptions)}
                ref={selectRef}
                style={{ minHeight: `${minHeight}px` }}
            >
                <div className={styles.selectedValue}>
                    {options.find(option => option.value === value)?.label || value}
                </div>
                <div className={`${styles.dropdownIcon} ${showOptions ? styles.open : ''}`}></div>
                {showOptions && (
                    <div className={styles.optionsList}>
                        {options.map((option, index) => (
                            <div
                                key={index}
                                className={`${styles.option} ${value === option.value ? styles.selected : ""}`}
                                onClick={() => handleSelectChange(option.value)}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div >
    );
};

export default SelectField;